"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import BrainDesktopDrop from "./BrainDesktopDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { QueryKey } from "../react-query-wrapper/ReactQueryWrapper";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import MemesQuickVoteDialog from "./left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import BrainMobileViewContent from "./mobile/BrainMobileViewContent";
import FloatingMemesQuickVoteTrigger from "./mobile/FloatingMemesQuickVoteTrigger";
import { BrainView } from "./mobile/brainMobileViews";
import { useBrainMobileActiveView } from "./mobile/useBrainMobileActiveView";
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(99);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const quickVote = useMemesQuickVoteDialogController();
  const hydrated = useSyncExternalStore(_temp2, _temp3, _temp4);
  const myStream = useMyStreamOptional();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("drop") ?? undefined;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dropId = t1;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t2;
  let t3;
  if ($[2] !== effectiveDropId) {
    t2 = [QueryKey.DROP, {
      drop_id: effectiveDropId
    }];
    t3 = async () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return await commonApiFetch({
        endpoint: `drops/${effectiveDropId}`
      });
    };
    $[2] = effectiveDropId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = !!effectiveDropId;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: keepPreviousData,
      enabled: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  if ($[9] !== myStream?.activeWave.id || $[10] !== pathname || $[11] !== searchParams) {
    t6 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[9] = myStream?.activeWave.id;
    $[10] = pathname;
    $[11] = searchParams;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const waveId = t6;
  let t7;
  if ($[13] !== isApp || $[14] !== pathname || $[15] !== router || $[16] !== searchParams) {
    t7 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[13] = isApp;
    $[14] = pathname;
    $[15] = router;
    $[16] = searchParams;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7 || $[19] !== waveId) {
    t8 = {
      waveId,
      onWaveNotFound: t7
    };
    $[18] = t7;
    $[19] = waveId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const {
    data: wave
  } = useWaveData(t8);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave,
    isDm
  } = useWave(wave);
  const {
    voting: t9,
    decisions: t10
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t9;
  const {
    firstDecisionDone
  } = t10;
  let t11;
  if ($[21] !== firstDecisionDone || $[22] !== isApp || $[23] !== isCompleted || $[24] !== isCurationWave || $[25] !== isMemesWave || $[26] !== isRankWave || $[27] !== pathname || $[28] !== searchParams || $[29] !== wave || $[30] !== waveId) {
    t11 = {
      firstDecisionDone,
      isApp,
      isCompleted,
      isCurationWave,
      isMemesWave,
      isRankWave,
      pathname,
      searchParams,
      wave,
      waveId
    };
    $[21] = firstDecisionDone;
    $[22] = isApp;
    $[23] = isCompleted;
    $[24] = isCurationWave;
    $[25] = isMemesWave;
    $[26] = isRankWave;
    $[27] = pathname;
    $[28] = searchParams;
    $[29] = wave;
    $[30] = waveId;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const {
    activeView,
    onViewChange
  } = useBrainMobileActiveView(t11);
  let t12;
  if ($[32] !== pathname || $[33] !== router || $[34] !== searchParams) {
    t12 = selectedDrop => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", selectedDrop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[32] = pathname;
    $[33] = router;
    $[34] = searchParams;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const onDropClick = t12;
  let t13;
  if ($[36] !== beginClosingDrop || $[37] !== dropId || $[38] !== pathname || $[39] !== router || $[40] !== searchParams) {
    t13 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[36] = beginClosingDrop;
    $[37] = dropId;
    $[38] = pathname;
    $[39] = router;
    $[40] = searchParams;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const onDropClose = t13;
  let t14;
  if ($[42] !== drop || $[43] !== effectiveDropId) {
    t14 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[42] = drop;
    $[43] = effectiveDropId;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const isDropOpen = t14;
  const hasWave = Boolean(waveId);
  let t15;
  if ($[45] !== pathname || $[46] !== router || $[47] !== searchParams) {
    t15 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname;
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[45] = pathname;
    $[46] = router;
    $[47] = searchParams;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const closeCreateOverlay = t15;
  let t16;
  bb0: {
    if (!isApp) {
      t16 = null;
      break bb0;
    }
    const createParam = searchParams.get("create");
    if (!createParam) {
      t16 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t16 = null;
      break bb0;
    }
    if (createParam === "dm") {
      let t17;
      if ($[49] !== closeCreateOverlay || $[50] !== connectedProfile) {
        t17 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[49] = closeCreateOverlay;
        $[50] = connectedProfile;
        $[51] = t17;
      } else {
        t17 = $[51];
      }
      t16 = t17;
      break bb0;
    }
    if (createParam === "wave") {
      let t17;
      if ($[52] !== closeCreateOverlay || $[53] !== connectedProfile) {
        t17 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[52] = closeCreateOverlay;
        $[53] = connectedProfile;
        $[54] = t17;
      } else {
        t17 = $[54];
      }
      t16 = t17;
      break bb0;
    }
    t16 = null;
  }
  const createOverlay = t16;
  const shouldMountFloatingQuickVoteEntry = isApp && hasWave && !!wave && activeView === BrainView.DEFAULT && !isDropOpen && !isDm;
  const shouldMountQuickVoteDialog = quickVote.isQuickVoteOpen || shouldMountFloatingQuickVoteEntry || activeView === BrainView.WAVES;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t17;
  if ($[55] !== drop || $[56] !== dropOverlayClass || $[57] !== isDropOpen || $[58] !== onDropClose) {
    t17 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[55] = drop;
    $[56] = dropOverlayClass;
    $[57] = isDropOpen;
    $[58] = onDropClose;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== activeView || $[61] !== hasWave || $[62] !== hydrated || $[63] !== isApp || $[64] !== onViewChange || $[65] !== wave) {
    t18 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={onViewChange} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[60] = activeView;
    $[61] = hasWave;
    $[62] = hydrated;
    $[63] = isApp;
    $[64] = onViewChange;
    $[65] = wave;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      opacity: 0,
      x: 20
    };
    t20 = {
      opacity: 1,
      x: 0
    };
    t21 = {
      opacity: 0,
      x: -20
    };
    t22 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[67] = t19;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
  } else {
    t19 = $[67];
    t20 = $[68];
    t21 = $[69];
    t22 = $[70];
  }
  let t23;
  if ($[71] !== quickVote.openQuickVote || $[72] !== quickVote.prefetchQuickVote || $[73] !== shouldMountFloatingQuickVoteEntry) {
    t23 = shouldMountFloatingQuickVoteEntry && <FloatingMemesQuickVoteTrigger onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} />;
    $[71] = quickVote.openQuickVote;
    $[72] = quickVote.prefetchQuickVote;
    $[73] = shouldMountFloatingQuickVoteEntry;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== activeView || $[76] !== children || $[77] !== isCurationWave || $[78] !== isMemesWave || $[79] !== isRankWave || $[80] !== onDropClick || $[81] !== quickVote.openQuickVote || $[82] !== quickVote.prefetchQuickVote || $[83] !== wave || $[84] !== waveId) {
    t24 = <BrainMobileViewContent activeView={activeView} activeWaveId={waveId} isCurationWave={isCurationWave} isMemesWave={isMemesWave} isRankWave={isRankWave} onDropClick={onDropClick} onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} wave={wave}>{children}</BrainMobileViewContent>;
    $[75] = activeView;
    $[76] = children;
    $[77] = isCurationWave;
    $[78] = isMemesWave;
    $[79] = isRankWave;
    $[80] = onDropClick;
    $[81] = quickVote.openQuickVote;
    $[82] = quickVote.prefetchQuickVote;
    $[83] = wave;
    $[84] = waveId;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  let t25;
  if ($[86] !== activeView || $[87] !== t23 || $[88] !== t24) {
    t25 = <AnimatePresence mode="wait"><motion.div key={activeView} initial={t19} animate={t20} exit={t21} transition={t22} className="tw-relative tw-min-w-0 tw-flex-1">{t23}{t24}</motion.div></AnimatePresence>;
    $[86] = activeView;
    $[87] = t23;
    $[88] = t24;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== quickVote.dialogState || $[91] !== shouldMountQuickVoteDialog) {
    t26 = shouldMountQuickVoteDialog && <MemesQuickVoteDialog {...quickVote.dialogState} />;
    $[90] = quickVote.dialogState;
    $[91] = shouldMountQuickVoteDialog;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== createOverlay || $[94] !== t17 || $[95] !== t18 || $[96] !== t25 || $[97] !== t26) {
    t27 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t17}{t18}{t25}{t26}</div>;
    $[93] = createOverlay;
    $[94] = t17;
    $[95] = t18;
    $[96] = t25;
    $[97] = t26;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  return t27;
};
export default BrainMobile;
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {
  return true;
}
function _temp4() {
  return false;
}